import { ReactNode } from "react";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface FieldStackProps {
  /** Field label text */
  label: string;
  /** Mark field as required (shows asterisk) */
  required?: boolean;
  /** Helper text below control */
  helper?: string;
  /** Error message (replaces helper when present) */
  error?: string;
  /** The form control (input, select, etc.) */
  children: ReactNode;
  /** HTML for attribute to link label with control */
  htmlFor?: string;
  /** Additional className for container */
  className?: string;
  /** Optional indicator rendered inline after label (e.g. dirty dot) */
  indicator?: ReactNode;
}

/**
 * FieldStack - Standard field layout primitive for sheet forms.
 * 
 * Rhythm: Label (text-sm) → Control → Helper/Error (text-xs)
 * Gap: gap-1 between label and control, gap-0.5 to helper
 * 
 * Part of Global Sheet Content System v1.
 * 
 * Usage:
 * <FieldStack label="Email" required error={errors.email}>
 *   <Input type="email" />
 * </FieldStack>
 */
export function FieldStack({
  label,
  required = false,
  helper,
  error,
  children,
  htmlFor,
  className,
  indicator,
}: FieldStackProps) {
  return (
    <div className={cn("space-y-1 overflow-hidden", className)}>
      <Label 
        htmlFor={htmlFor} 
        className={cn(
          "text-sm font-medium flex items-center gap-1.5",
          error && "text-destructive"
        )}
      >
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
        {indicator}
      </Label>
      
      {children}
      
      {(error || helper) && (
        <p className={cn(
          "text-xs",
          error ? "text-destructive" : "text-muted-foreground"
        )}>
          {error || helper}
        </p>
      )}
    </div>
  );
}
